import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Vue} from 'vue-property-decorator';
import {PermissionState} from '@capacitor/core';
import {EventsService, IBaseResponse, TOrderIcon, WebService} from '../../services';
import {Platform} from 'quasar';
import {TBrand, TDiscountTarget} from '..';
import {Position} from '@capacitor/geolocation';

/**
 * This object must be kept in sync manually with the CUSTOMER_PORTAL_APP_FEATURE_FLAG
 * defined in the server CustomerPortalAppFeatureFlag file
 * (server/app/src/constants/CustomerPortalAppFeatureFlag.ts), as well as any other
 * properties added to BrandConfig's featureFlags list.
 */
export const FeatureFlag = {
	PUSH_NOTIFICATIONS: 'PUSH_NOTIFICATIONS',
	GIFT_CARD_RELOAD: 'GIFT_CARD_RELOAD',
	ORDERING: 'ORDERING',
	SHARE_URL: 'SHARE_URL',
} as const;
export type TFeatureFlag = typeof FeatureFlag[keyof typeof FeatureFlag];

export interface IFeatureFlag {
	name: TFeatureFlag;
	enabled: boolean;
}

export interface IAuthConfig {
	userBase: string | null;
	franchisorBase: string | null;
	apiKey: string | null;
}

export interface IVisitedLocation {
	id: string;
	name: string;
	latitude: number;
	longitude: number;
	lastVisit: number;
	visits: number;
}

export interface IOpportunity {
	id: string;
	name: string;
	description: string;
	image: string | null;
	expires: number;
	created: number;
}

export interface INPSRatingResponse {
	start: number;
	end: number;
	responseText: string;
	thankYouText: string;
}

export interface INPSSurvey {
	avatarImage: string;
	backgroundColor: string;
	textColor: string;
	introductionText: string;
	npsQuestion: string;
	ratingResponses: INPSRatingResponse[];
	rewardPoints: number;
	rewardText: string;
}

export interface INPSOpportunity {
	id: string;
	expires: number;
	npsSurvey: INPSSurvey;
}

export interface IOfferItem {
	base: string;
	itemId: string;
	optionId: string | null;
	choiceIds: string[];
}

export interface IOffer {
	id: number;
	name: string;
	description: string;
	image: string | null;
	expires: number | null;
	locations: null | string[];
	target: TDiscountTarget;
	items: IOfferItem[];
	flags: number; // Bitmask of TRewardFlag/REWARD_FLAG_* values
	modDate: number | null;
}

export interface SidebarLink {
	title: string;
	url: string;
}

export interface IBrandConfigColors {
	actionPrimaryColor: string;
	backgroundColor: string;
	backgroundSecondaryColor: string;
	bootScreenColor: string;
	fullscreenBackgroundColor: string;
	giftCardColor: string;
	memberCardColor: string;
	navColor: string;
	pointsTextColor: string;
	qrPageColor: string;
	rewardClaimedScreenColor: string;
	singleHighlightColor: string;
}

export interface IBrandConfig extends IBrandConfigColors {
	id: string;
	brandName: string;
	cardName: string;
	// This is still returned by the API, but we don't use it.
	// We will remove it from the API in the future.
	// We are leaving this here so if you notice locations is
	// returned but not set, you know why.
	// locations: ILocationLite[];
	logoImage: string;
	rewardImage: string;
	memberCardBackgroundImage: string;
	giftCardBackgroundImage: string;
	loginBackgroundImage: string;
	qrPageBackgroundImage: string;
	loyaltyInfoBackgroundImage: string;
	defaultOfferImage: string | null;
	backgroundImages: string[];
	drinkImages: string[];
	qrCardBackgroundImages: string[];
	loyaltyVisitsPerReward: number;
	loyaltyPointsPerReward: number;
	loyaltyPointsRewardName: string;
	loyaltySignupRewardOn: boolean;
	loyaltySignupRewardName: string;
	loyaltySignupRewardImage: string | null;
	loyaltySignupRewardDaysValid: number;
	loyaltyAppInstallRewardOn: boolean;
	loyaltyAppInstallRewardName: string;
	loyaltyAppInstallRewardDescription: string;
	loyaltyAppInstallRewardImage: string | null;
	appFont: string;
	largeTextFont: string;
	orderIcon: TOrderIcon;
	appMenuId: string;
	sidebarLinks: SidebarLink[];
	customerPortalUrl: string;
	customerPortalUrlScheme: string;
	giftCardLocationId: string;
	googleMapsApiKey: string | null;
	postHogApiKey: string | null;
	featureFlags: IFeatureFlag[];
	onlineStoreLink: string | null;
	pointsDisplay: string | null;
	useLineItemQuantities: boolean;
	// menu: ISimpleMenu | null;
	serviceDescriptions: Record<string, string>;
}

export type TEnvironment = 'default' | 'development' | 'development_direct' | 'qa' | 'qa_direct' | 'production' | 'production_direct';

export type TCardType = 'member' | 'giftCard';

export enum EPointsDisplayOptions {
	STACKED = 'stacked',
	PLAIN = 'plain',
	CUSTOMER_CHOICE = 'customer_choice',
}

export type TPointsDisplay = EPointsDisplayOptions.STACKED | EPointsDisplayOptions.PLAIN;

export const Permission = {
	PUSH_NOTIFICATIONS: 'PUSH_NOTIFICATIONS',
	LOCATION: 'LOCATION',
} as const;
export type TPermission = typeof Permission[keyof typeof Permission];

export interface ISessionStoreState {
	authConfig: IAuthConfig | null;
	tempAuthConfig: IAuthConfig | null;
	brandConfig: IBrandConfig | null;
	selectedBackground: number;
	selectedEnvironment: TEnvironment;
	currentCard: TCardType;
	requestedPermissions: Record<string, boolean>;
	permissionStates: Record<string, string>;
	flags: Record<string, boolean>;
	lastLocation: Position | null;
	userSelectedPointsDisplay: TPointsDisplay;
}

@Module({name: 'session', namespaced: true})
class SessionModule extends VuexModule implements ISessionStoreState {
	initialPath: string | null = null;
	authConfig: IAuthConfig | null = null;
	tempAuthConfig: IAuthConfig | null = null;
	brandConfig: IBrandConfig | null = null;
	selectedBackground = 0;
	selectedEnvironment: TEnvironment = 'default';
	userSelectedPointsDisplay: TPointsDisplay = EPointsDisplayOptions.STACKED;
	currentCard: TCardType = 'member';
	requestedPermissions: Record<string, boolean> = {};
	permissionStates: Record<string, string> = {};
	flags: Record<string, boolean> = {};
	lastLocation: Position | null = null;
	selectedDrinkImageIndex = 0;
	selectedQrCardBackgroundImageIndex = 0;
	brand: TBrand | null = null;
	lastOrderTab = '';

	get isBrand() {
		return (brand: TBrand) => {
			return this.brand === brand;
		};
	}

	get selectedDrinkImage() {
		if(!this.brandConfig) {
			return null;
		}

		if(this.brandConfig.drinkImages.length === 0) {
			return null;
		}

		let index = this.selectedDrinkImageIndex;
		if(isNaN(index) || index >= this.brandConfig.drinkImages.length) {
			index = 0;
		}

		return this.brandConfig.drinkImages[index];
	}

	get selectedQrCardBackgroundImage() {
		if(!this.brandConfig) {
			return null;
		}

		if(this.brandConfig.qrCardBackgroundImages.length === 0) {
			return null;
		}

		let index = this.selectedQrCardBackgroundImageIndex;
		if(isNaN(index) || index >= this.brandConfig.qrCardBackgroundImages.length) {
			index = 0;
		}

		return this.brandConfig.qrCardBackgroundImages[index];
	}

	get hasBeenRequested() {
		return (key: TPermission) => {
			return this.requestedPermissions && key in this.requestedPermissions && this.requestedPermissions[key];
		};
	}

	get isEnabled() {
		return (name: TFeatureFlag) => {
			if(this.flags && name in this.flags) {
				return this.flags[name];
			}
			// Assume things are not enabled if not set
			return false;
		};
	}

	get permissionState() {
		return (name: TPermission) => {
			if(this.permissionStates && name in this.permissionStates) {
				return this.permissionStates[name];
			}
			// Assume things are not enabled if not set
			return 'unknown';
		};
	}

	get showOnlineStoreLink() {
		return this.brandConfig && this.brandConfig.onlineStoreLink;
	}

	get openOnlineStore() {
		return () => {
			if(this.brandConfig && 'onlineStoreLink' in this.brandConfig && this.brandConfig.onlineStoreLink !== null) {
				const url = new URL(this.brandConfig.onlineStoreLink);
				const urlParams = new URLSearchParams(url.search);
				urlParams.set('auth', btoa(JSON.stringify(this.authConfig)));
				url.search = urlParams.toString();
				url.pathname = '/home';
				window.open(url.toString(), '_blank');
			}
		};
	}

	get selectedPointsDisplay() {
		if(this.brandConfig?.pointsDisplay === EPointsDisplayOptions.CUSTOMER_CHOICE) {
			return this.userSelectedPointsDisplay || EPointsDisplayOptions.STACKED;
		}
		return this.brandConfig?.pointsDisplay || EPointsDisplayOptions.STACKED;
	}

	@Action
	async fetchBrandConfig() {
		const headers: Record<string, any> = {};
		if(this.authConfig) {
			headers.API_KEY = this.authConfig.apiKey;
			headers.CUSTOMER_BASE = this.authConfig.userBase;
		}
		const response = await WebService.unauthenticatedClient.get<IBaseResponse<{config: IBrandConfig}>>('config', {
			params: {
				domain: await WebService.getDomain()
			},
			headers: headers
		});
		this.setBrandConfig(response.data.data.config);

		EventsService.init(response.data.data.config.postHogApiKey);

		// We also need to grab the feature flags off and store them at the top level
		this.clearFeatureFlags();

		// Set flags from server
		if(response.data.data.config.featureFlags) {
			for(const flag of response.data.data.config.featureFlags) {
				this.setFeatureFlag(flag);
			}
		}

		// Until we get Google Pay working we need to turn this off on Android/Chrome
		if(Platform.is.android || Platform.is.chrome) {
			this.setFeatureFlag({
				name: FeatureFlag.GIFT_CARD_RELOAD,
				enabled: false
			});
		}

		// Code to support a publicly available menu if pushed by apple again
		// if(response.data.data.config.menu) {
		// 	MenuStore.setMenu(response.data.data.config.menu);
		// }
	}

	@Action
	public async init() {
		this.INIT();
		return Promise.resolve();
	}

	@Mutation
	public INIT() {
		if(!this.selectedDrinkImageIndex) {
			this.selectedDrinkImageIndex = 0;
		}
		if(!this.selectedQrCardBackgroundImageIndex) {
			this.selectedQrCardBackgroundImageIndex = 0;
		}
	}

	@Mutation
	public setInitialPath(initialPath: string | null) {
		if(
			initialPath === null ||
			(
				initialPath !== '/' &&
				initialPath !== '/launch' &&
				initialPath.substr(0, 2) !== '/?'
			)
		) {
			this.initialPath = initialPath;
		}
	}

	@Mutation
	public setLastLocation(location: Position | null) {
		this.lastLocation = location;
	}

	@Mutation
	public setFeatureFlag(flag: IFeatureFlag) {
		if(!this.flags) {
			this.flags = {};
		}
		Vue.set(this.flags, flag.name, flag.enabled);
	}

	@Mutation
	public setPermissionState(update: {name: TPermission; state: PermissionState}) {
		if(!this.permissionStates) {
			this.permissionStates = {};
		}
		Vue.set(this.permissionStates, update.name, update.state);
	}

	@Mutation
	public clearFeatureFlags() {
		this.flags = {};
	}

	@Mutation
	public setAuthConfig(authConfig: IAuthConfig) {
		this.authConfig = authConfig;
	}

	@Mutation
	public setTempAuthConfig(authConfig: IAuthConfig) {
		this.tempAuthConfig = authConfig;
	}

	@Mutation
	public clearAuthConfig() {
		this.authConfig = null;
	}

	@Mutation
	public clearTempAuthConfig() {
		this.tempAuthConfig = null;
	}

	@Mutation
	public setBrandConfig(brandConfig: IBrandConfig) {
		this.brandConfig = brandConfig;
	}

	@Mutation
	public clearBrandConfig() {
		this.brandConfig = null;
	}

	@Mutation
	public setSelectedBackground(selectedBackground: number) {
		this.selectedBackground = selectedBackground;
	}

	@Mutation
	public setEnvironment(environment: TEnvironment) {
		this.selectedEnvironment = environment;
		WebService.restart();
	}

	@Mutation
	public setCurrentCard(cardType: TCardType) {
		this.currentCard = cardType;
	}

	@Mutation
	public setRequested(key: TPermission) {
		if(!this.requestedPermissions) {
			this.requestedPermissions = {};
		}
		Vue.set(this.requestedPermissions, key, true);
	}

	@Mutation
	public clearAllRequested() {
		this.requestedPermissions = {};
	}

	@Mutation
	public setSelectedDrinkImageIndex(index: number) {
		this.selectedDrinkImageIndex = index;
	}

	@Mutation
	public setSelectedQrCardBackgroundImageIndex(index: number) {
		this.selectedQrCardBackgroundImageIndex = index;
	}

	@Mutation
	public setBrand(brand: TBrand) {
		this.brand = brand;
	}

	@Mutation
	public setLastOrderTab(value: string) {
		this.lastOrderTab = value;
	}

	@Mutation
	public clearLastOrderTab() {
		this.lastOrderTab = '';
	}

	@Mutation
	public setPointsDisplayStyle(value: TPointsDisplay) {
		this.userSelectedPointsDisplay = value;
	}
}


export {
	SessionModule
};
